import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import MenuStroke from '@/assets/icons/menu_stroke.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { useLayout } from '@/contexts/layout';

import styles from './AppHeader.module.scss';

export interface AppHeaderProps {
    /** Content rendered on the left side (e.g., breadcrumbs) */
    left?: ReactNode;
    /** Content rendered on the right side (e.g., language selector, user menu) */
    right?: ReactNode;
    /** Override mobile menu visibility (defaults to LayoutContext isMobile) */
    showMobileMenu?: boolean;
    /** Additional CSS class for the root element */
    className?: string;
}

/**
 * Application header component designed for the AppLayout grid system.
 *
 * Provides a generic two-section layout:
 * - Left: mobile hamburger toggle + any content (typically breadcrumbs)
 * - Right: any content (typically language selector, user menu)
 *
 * The parent is responsible for composing the actual content passed
 * into each side. This keeps AppHeader agnostic of specific child components.
 *
 * Integrates with LayoutContext for sidebar toggle on mobile.
 * Should be passed as the `header` prop of AppLayout.
 */
export default function AppHeader({ left, right, showMobileMenu, className }: AppHeaderProps) {
    const { t } = useTranslation();
    const { state, actions: layoutActions } = useLayout();
    const { isMobile, sidebarOpen } = state;

    const showHamburger = showMobileMenu ?? isMobile;

    return (
        <div className={cn(styles.appHeader, className)}>
            <div className={styles.leftSection}>
                {showHamburger && (
                    <button
                        type="button"
                        className={styles.menuButton}
                        onClick={layoutActions.toggleSidebar}
                        aria-label={t('layout.appHeader.toggleNavigation')}
                        aria-expanded={sidebarOpen}>
                        <SvgIcon
                            icon={MenuStroke}
                            size="custom"
                            customSize={20}
                            className={styles.menuIcon}
                            svgProps={{ 'aria-hidden': true }}
                        />
                    </button>
                )}

                {left && <div className={styles.leftContent}>{left}</div>}
            </div>

            {right && <div className={styles.rightSection}>{right}</div>}
        </div>
    );
}
